import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  AlertTriangle,
  User,
  Globe,
  Factory,
  Loader2
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle } from 'lucide-react';
import {
  faBuilding,
  faBuildings,
  faCheck,
  faChevronDown,
  faCity,
  faGraduationCap,
  faPlus,
  faShield,
  faUser,
  faUsers,
  faX
} from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { getDocs, collection, doc, getDoc } from 'firebase/firestore';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import StripePaymentForm from './StripePaymentForm';
import PaymentHandler from './PaymentHandler';
import { TenancyCreationDialog, SetupPhase } from './TenancyCreationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
// import { useFirebaseAuth } from '@/js/firebase/useFirebaseAuth';
import { auth, db } from '@/lib/firebase';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { listFrameworks } from '@/services/frameworkService';
import { Framework as BaseFramework } from '@/services/frameworkService';
import { useUserRegistration } from '@/hooks/useUserRegistration';
import { UserRegistrationData } from '@/services/userRegistrationService';
import { stripePaymentService, PaymentData } from '@/services/stripePaymentService';
import { AuthProvider } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PhoneInput } from '@/components/ui/phone-input';
import '@/styles/global.css';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { toast } from 'sonner';
import { isFeatureEnabled } from '@/utils/featuresUtil';

// Initialize Stripe with your publishable key
// NOTE: Replace this with your actual publishable key from Stripe dashboard
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

// Enhanced framework for UI with needed properties
interface UIFrameworkExtensions {
  id: string; // Already exists in base but explicit here
  description: string;
  iconName?: string; // Name of the icon (e.g. 'shield', 'building')
  icon?: IconDefinition; // For backwards compatibility
  tags: string[];
  logo?: string;
}

// Combine base Framework with UI extensions
type Framework = BaseFramework & UIFrameworkExtensions;

// Define the Plan type
type Plan = {
  id: string;
  name: string;
  description: string;
  icon: string;
  features: string[];
  order: number;
};

// Password Requirements component
const PasswordRequirements = ({ password, confirmPassword = '' }: { password: string; confirmPassword?: string }) => {
  const requirements = [
    { label: 'At least 8 characters long', met: password.length >= 8 },
    { label: 'Contains at least one number', met: /\d/.test(password) },
    { label: 'Contains at least one uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Contains at least one lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Contains at least one special character (!@#$%^&*(),.?":{}|<>_)', met: /[!@#$%^&*(),.?":{}|<>_]/.test(password) },
  ];

  const strengthRules = requirements;
  const metCount = strengthRules.filter((r) => r.met).length;
  const strengthPct = Math.round((metCount / strengthRules.length) * 100);
  const strengthLabel =
    metCount <= 1 ? 'Very weak' : metCount === 2 ? 'Weak' : metCount === 3 ? 'Fair' : metCount === 4 ? 'Good' : 'Strong';

  return (
    <Card aria-live="polite" className="w-full mx-auto mt-2 mb-4 p-4 shadow-sm border bg-card/80 backdrop-blur">
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-muted-foreground">Password strength</span>
          <span
            className={cn(
              'text-sm font-medium',
              metCount < 3 ? 'text-amber-600' : metCount < 5 ? 'text-green-600' : 'text-green-700'
            )}
          >
            {strengthLabel}
          </span>
        </div>
        <Progress value={strengthPct} className="h-2" />
      </div>
      <ul className="space-y-2">
        {requirements.map((req, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            {req.met ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 transition-transform duration-200" />
            ) : (
              <Circle className="w-5 h-5 text-muted-foreground" />
            )}
            <span className={cn('transition-colors', req.met ? 'text-foreground' : 'text-muted-foreground')}>
              {req.label}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// Spinner component for loading states
const Spinner = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClass = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  }[size];

  return (
    <div className="flex justify-center items-center p-2">
      <div className={`animate-spin rounded-full border-2 border-t-transparent ${sizeClass}`}></div>
    </div>
  );
};

export type OnboardingVariant = 'default' | 'iso';

type Props = {
  variant?: OnboardingVariant;
  onComplete?: () => void;
};

const createInitialFormData = () => ({
  teamSize: '2-10',
  yourName: '',
  email: '',
  password: '',
  confirmPassword: '',
  phoneNumber: '',
  companyPlan: '',
  companyName: '',
  industry: '',
  website: '',
  nameOnCard: '',
  cardValid: false,
  saveCard: true,
  selectedFrameworks: [] as string[],
  moduleLevels: {} as Record<string, any>,
  enterpriseChoice: undefined as 'meeting' | 'payment' | undefined,
  meetingDate: '',
  meetingTime: '',
  meetingTimeZone: '',
  meetingPreferences: '',
  paymentIntentId: undefined as string | undefined
});

const getSteps = (isEnterprise: boolean, hasModulesWithLevels: boolean) => {
  const baseSteps = [
    { id: 1, title: 'Account Info', description: 'Setup your account settings' },
    { id: 2, title: 'Select Frameworks', description: 'Select the frameworks you want to use' },
  ];
  
  // Add module levels step if needed (after business details)
  if (hasModulesWithLevels) {
    baseSteps.push({ id: 3, title: 'Module Levels', description: 'Configure compliance levels' });
  }
  
  // Add billing/meeting step (always after module levels or business details)
  const billingStepId = hasModulesWithLevels ? 4 : 3;
  baseSteps.push({
    id: billingStepId,
    title: isEnterprise ? 'Schedule Meeting' : 'Billing Details',
    description: isEnterprise ? 'Schedule a consultation with our team' : 'Provide your payment info'
  });
  
  // Add completion step (always last)
  const completionStepId = hasModulesWithLevels ? 5 : 4;
  baseSteps.push({
    id: completionStepId,
    title: 'Completed',
    description: 'Your account is created'
  });
  
  return baseSteps;
};

export default function OnboardingFlow({ variant = 'default', onComplete }: Props) {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [passwordFocused, setPasswordFocused] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
  const [showTenancyDialog, setShowTenancyDialog] = useState<boolean>(false);
  const [setupPhase, setSetupPhase] = useState<SetupPhase>('account');
  const [accountProgress, setAccountProgress] = useState<number>(0);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [fixWebsiteDialogOpen, setFixWebsiteDialogOpen] = useState<boolean>(false);
  const [websiteFixMessage, setWebsiteFixMessage] = useState<string>('');
  const [websiteFixValue, setWebsiteFixValue] = useState<string>('');
  const [existingAuthedUser, setExistingAuthedUser] = useState<boolean>(false);
  const [accountEditDialogOpen, setAccountEditDialogOpen] = useState<boolean>(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const navigate = useNavigate();

  // User registration hook
  const {
    registerWithEmail,
    isRegistering: registrationInProgress,
    isSuccess: registrationSuccess,
    isError: registrationError,
    error: registrationErrorMessage,
    result: registrationResult,
    reset
  } = useUserRegistration();
  const [formData, setFormData] = useState(createInitialFormData);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [accountEditDraft, setAccountEditDraft] = useState({
    companyName: createInitialFormData().companyName,
    teamSize: createInitialFormData().teamSize,
    companyPlan: createInitialFormData().companyPlan,
    website: createInitialFormData().website,
  });

  // Determine if enterprise plan is selected
  const isEnterprisePlan = formData.companyPlan === 'enterprise';

  // Check if any selected frameworks have levels
  const hasModulesWithLevels = frameworks
    .filter(f => formData.selectedFrameworks.includes(f.id))
    .some(f => (f as any).levels || (f as any).levelCategories);

  // Get dynamic steps based on plan selection and module levels
  const steps = getSteps(isEnterprisePlan, hasModulesWithLevels);

  // Helper to get step numbers for specific purposes
  const getStepNumber = (purpose: 'account' | 'business' | 'moduleLevels' | 'billing' | 'completion') => {
    switch (purpose) {
      case 'account': return 1;
      case 'business': return 2;
      case 'moduleLevels': return hasModulesWithLevels ? 3 : -1;
      case 'billing': return hasModulesWithLevels ? 4 : 3;
      case 'completion': return hasModulesWithLevels ? 5 : 4;
      default: return -1;
    }
  };

  const businessStepNumber = getStepNumber('business');

  // Function to fetch plans from Firebase Firestore
  const fetchPlans = async () => {
    try {
      // Reference to the plans collection
      const plansRef = collection(db, 'plans');
      const plansSnapshot = await getDocs(plansRef);

      // Map the documents to the Plan type, skipping the metadata document
      const plansData = plansSnapshot.docs
        .filter(doc => doc.id !== 'metadata') // Skip the metadata document
        .map(doc => ({
          id: doc.id,
          name: doc.data().name || '',
          description: doc.data().description || '',
          icon: doc.data().icon || 'faBuilding',
          features: doc.data().features || [],
          order: doc.data().order || 0
        } as Plan))
        .filter(plan => {
          // Filter plans based on feature flags
          // Business Plan requires 'Onboarding Business Plan' feature
          if (plan.id === 'business' || plan.name.toLowerCase().includes('business')) {
            return isFeatureEnabled('Onboarding Business Plan');
          }
          // Enterprise Plan requires 'Onboarding Enterprise Plan' feature
          if (plan.id === 'enterprise' || plan.name.toLowerCase().includes('enterprise')) {
            return isFeatureEnabled('Onboarding Enterprise Plan');
          }
          // All other plans are shown by default
          return true;
        })
        .sort((a, b) => a.order - b.order);

      console.log('Fetched plans:', plansData);
      setPlans(plansData);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  // on mount
  useEffect(() => {
    // Fetch plans from Firebase
    fetchPlans();
    listFrameworks().then((frameworksData) => {
      // Map frameworks to include default UI properties if missing
      const enhancedFrameworks = frameworksData.map(fw => {
        // Create a properly typed framework object
        const enhancedFw = {
          // Base properties from the service
          ...fw,
          id: fw.slug || '',
          displayName: fw.displayName || '',
          // UI-specific properties with defaults
          description: (fw as any).description || `Framework for ${fw.displayName || 'Compliance'}`,
          icon: faShield,
          tags: Array.isArray((fw as any).tags) ? (fw as any).tags : ['Compliance']
        } as Framework;
        return enhancedFw;
      });

      console.log('Enhanced frameworks:', enhancedFrameworks);
      setFrameworks(enhancedFrameworks);
    });
  }, []);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) {
        return;
      }

      if (!firebaseUser) {
        setExistingAuthedUser(false);
        return;
      }

      try {
        setFormData((prev) => ({
          ...prev,
          email: prev.email || firebaseUser.email || '',
          yourName: prev.yourName || firebaseUser.displayName || prev.yourName,
        }));

        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        const userData = userDocSnap.exists() ? userDocSnap.data() : null;
        const organisations = Array.isArray(userData?.organisations) ? userData?.organisations : [];

        if (organisations.length > 0) {
          navigate('/dashboard');
          return;
        }

        setExistingAuthedUser(true);
        setCurrentStep((step) => (step < businessStepNumber ? businessStepNumber : step));
      } catch (error) {
        console.error('Failed to evaluate existing onboarding state:', error);
        setExistingAuthedUser(true);
        setCurrentStep((step) => (step < businessStepNumber ? businessStepNumber : step));
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [businessStepNumber, navigate]);

  // State for form validation
  const [validationError, setValidationError] = useState<string>('');
  const [cardValid, setCardValid] = useState<boolean>(false);
  
  // Field-level validation errors for all required fields
  const [fieldErrors, setFieldErrors] = useState<{
    yourName?: string;
    phoneNumber?: string;
    email?: string;
    password?: string;
    companyName?: string;
    website?: string;
    teamSize?: string;
    companyPlan?: string;
    nameOnCard?: string;
  }>({});

  // Payment handlers
  const handlePaymentStart = () => {
    setIsProcessingPayment(true);
    setValidationError('');
  };

  const handlePaymentSuccess = (paymentIntentId?: string) => {
    setIsProcessingPayment(false);
    setPaymentIntentId(paymentIntentId || null);
    // Add payment intent ID to form data before showing tenancy dialog
    setFormData(prev => ({ ...prev, paymentIntentId: paymentIntentId || undefined }));

    // After successful payment, show the dialog immediately and track progress
    (async () => {
      if (!existingAuthedUser) {
        // Reset and show dialog in account phase
        setSetupPhase('account');
        setAccountProgress(0);
        setAccountError(null);
        setShowTenancyDialog(true);
        setIsRegistering(true);
        setValidationError('');

        try {
          // Simulate progress steps for account creation
          setAccountProgress(10); // Starting
          
          const nameParts = formData.yourName.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          setAccountProgress(25); // Preparing data

          const registrationData: UserRegistrationData = {
            firstName,
            lastName,
            email: formData.email,
            password: formData.password,
            phoneNumber: formData.phoneNumber
          };

          setAccountProgress(40); // Validating

          const result = await registerWithEmail(registrationData);

          setAccountProgress(80); // Processing response

          if (!result.success) {
            setAccountError(result.error || 'Registration failed. Please try again.');
            setIsRegistering(false);
            return;
          }

          setAccountProgress(100); // Complete
          
          // Small delay to show 100% before transitioning
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (error) {
          console.error('Registration error after payment:', error);
          setAccountError('Registration failed. Please try again.');
          setIsRegistering(false);
          return;
        }

        setIsRegistering(false);
      } else {
        // User already authenticated, skip to tenancy phase
        setSetupPhase('tenancy');
        setShowTenancyDialog(true);
        return;
      }

      // Transition to tenancy creation phase
      setSetupPhase('tenancy');
    })();
  };

  const handlePaymentError = (error: string) => {
    setIsProcessingPayment(false);
    setValidationError(error);
  };

  const handleFixRequested = useCallback((details: { field: 'website' | 'email' | 'framework' | 'nameOnCard' | 'account' | 'business' | 'billing'; message: string; currentValue: string }) => {
    // Map field types to their corresponding steps
    const fieldToStep: Record<string, number> = {
      'email': getStepNumber('account'),
      'account': getStepNumber('account'),
      'website': getStepNumber('business'),
      'business': getStepNumber('business'),
      'framework': getStepNumber('business'),
      'nameOnCard': getStepNumber('billing'),
      'billing': getStepNumber('billing'),
    };

    // For website errors, show the fix dialog
    if (details.field === 'website') {
      setWebsiteFixMessage(details.message);
      setWebsiteFixValue(details.currentValue);
      setFixWebsiteDialogOpen(true);
      return;
    }

    // For email/account errors when user already has an account, show the account edit dialog
    // since they can't change their email without restarting onboarding
    if ((details.field === 'email' || details.field === 'account') && existingAuthedUser) {
      setAccountEditDialogOpen(true);
      setValidationError(details.message);
      return;
    }

    // For other errors, navigate to the appropriate step and show validation error
    const targetStep = fieldToStep[details.field];
    if (targetStep) {
      setCurrentStep(targetStep);
      setValidationError(details.message);
    }
  }, [getStepNumber, existingAuthedUser]);

  const handleApplyWebsiteFix = useCallback(() => {
    const trimmed = websiteFixValue.trim();
    if (!trimmed) {
      return;
    }
    setFormData(prev => ({ ...prev, website: trimmed }));
    setFixWebsiteDialogOpen(false);
    setWebsiteFixMessage('');
    setShowTenancyDialog(true);
  }, [websiteFixValue]);

  const handleCancelWebsiteFix = useCallback(() => {
    setFixWebsiteDialogOpen(false);
    setWebsiteFixMessage('');
  }, []);

  const triggerDashboardRefresh = useCallback(async () => {
    try {
      await deiterateAPI.POST('/dashboard/refresh');
      toast.success('Dashboard data refresh triggered.');
    } catch (error) {
      console.error('Failed to trigger dashboard refresh:', error);
      toast.error('Failed to refresh dashboard data.');
    }
  }, []);

  useEffect(() => {
    if (accountEditDialogOpen) {
      setAccountEditDraft({
        companyName: formData.companyName,
        teamSize: formData.teamSize,
        companyPlan: formData.companyPlan,
        website: formData.website,
      });
    }
  }, [accountEditDialogOpen, formData.companyName, formData.teamSize, formData.companyPlan, formData.website]);

  const handleAccountEditDraftChange = useCallback(<T extends keyof typeof accountEditDraft>(field: T, value: (typeof accountEditDraft)[T]) => {
    setAccountEditDraft(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const normalizeWebsite = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const handleApplyAccountEdits = useCallback(() => {
    const normalizedWebsite = normalizeWebsite(accountEditDraft.website);

    setFormData(prev => ({
      ...prev,
      companyName: accountEditDraft.companyName,
      teamSize: accountEditDraft.teamSize,
      companyPlan: accountEditDraft.companyPlan,
      website: normalizedWebsite,
      enterpriseChoice: accountEditDraft.companyPlan === 'enterprise' ? prev.enterpriseChoice : undefined,
    }));

    setAccountEditDialogOpen(false);
    setValidationError('');
  }, [accountEditDraft, normalizeWebsite]);

  const handleRestartOnboarding = useCallback(async () => {
    setAccountEditDialogOpen(false);
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Failed to sign out during onboarding restart:', error);
    }
    setExistingAuthedUser(false);
    reset();
    setFormData(createInitialFormData());
    setValidationError('');
    setShowTenancyDialog(false);
    setPaymentIntentId(null);
    setCurrentStep(getStepNumber('account'));
  }, [reset]);

  // Validate fields for the current step
  const validateCurrentStep = (): boolean => {
    setValidationError('');

    // Account Info
    if (currentStep === getStepNumber('account')) {
      if (existingAuthedUser) {
        return true;
      }
      
      // Collect all validation errors
      const errors = [];
      
      // Check required fields
      if (!formData.yourName?.trim()) {
        errors.push('missing name');
      }
      if (!formData.email?.trim()) {
        errors.push('missing email');
      } else {
        // Validate email format if provided
        const emailError = validateEmail(formData.email);
        if (emailError) {
          errors.push(emailError.toLowerCase());
        }
      }
      if (!formData.password?.trim()) {
        errors.push('missing password');
      } else if (!passwordMeetsRequirements(formData.password)) {
        errors.push('weak password');
      }
      if (!formData.phoneNumber?.trim()) {
        errors.push('missing phone number');
      }
      if (!formData.companyName?.trim()) {
        errors.push('missing company name');
      }
      if (!formData.website?.trim()) {
        errors.push('missing website');
      } else {
        // Validate website format if provided
        const websiteError = validateWebsite(formData.website);
        if (websiteError) {
          errors.push(websiteError.toLowerCase());
        }
      }
      if (!formData.teamSize) {
        errors.push('missing team size');
      }
      if (!formData.companyPlan) {
        errors.push('missing company plan');
      }
      
      // Update field-level errors for all fields
      const yourNameError = !formData.yourName?.trim() ? 'Name is required' : undefined;
      const phoneNumberError = !formData.phoneNumber?.trim() ? 'Phone number is required' : undefined;
      const emailError = !formData.email?.trim() ? 'Email is required' : validateEmail(formData.email);
      const passwordError = !formData.password?.trim() 
        ? 'Password is required' 
        : (!passwordMeetsRequirements(formData.password) ? 'Password does not meet requirements' : undefined);
      const companyNameError = !formData.companyName?.trim() ? 'Company name is required' : undefined;
      const websiteError = !formData.website?.trim() ? 'Website is required' : validateWebsite(formData.website);
      const teamSizeError = !formData.teamSize ? 'Team size is required' : undefined;
      const companyPlanError = !formData.companyPlan ? 'Company plan is required' : undefined;
      
      setFieldErrors({
        yourName: yourNameError,
        phoneNumber: phoneNumberError,
        email: emailError || undefined,
        password: passwordError,
        companyName: companyNameError,
        website: websiteError || undefined,
        teamSize: teamSizeError,
        companyPlan: companyPlanError
      });
      
      if (errors.length > 0) {
        // Don't set bottom validation error - only use field-level errors
        return false;
      }
      return true;
    }

    // Business Details & Frameworks
    if (currentStep === getStepNumber('business')) {
      if (formData.selectedFrameworks.length === 0) {
        setValidationError('Please select at least one framework');
        return false;
      }
      return true;
    }

    // Module Levels (if applicable)
    if (currentStep === getStepNumber('moduleLevels') && hasModulesWithLevels) {
      // Validate that all modules with levels have their levels configured
      const modulesWithLevels = frameworks.filter(f => 
        formData.selectedFrameworks.includes(f.id) && 
        ((f as any).levels || (f as any).levelCategories)
      );
      
      for (const module of modulesWithLevels) {
        if (!formData.moduleLevels[module.id]) {
          setValidationError(`Please configure levels for ${module.displayName}`);
          return false;
        }
      }
      return true;
    }

    // Billing or meeting scheduling
    if (currentStep === getStepNumber('billing')) {
        const isEnterprisePlan = formData.companyPlan === 'enterprise';

        if (isEnterprisePlan) {
          console.log('Enterprise plan selected', formData);
          // Check which enterprise option was chosen
          if (!formData.enterpriseChoice) {
            setValidationError('Please select either meeting scheduling or immediate payment');
            return false;
          }

          if (formData.enterpriseChoice === 'meeting') {
            // Validate meeting scheduling fields
            if (!formData.meetingDate || !formData.meetingTime) {
              setValidationError('Please select a meeting date and time');
              return false;
            }
          } else if (formData.enterpriseChoice === 'payment') {
            // Validate billing fields for enterprise immediate payment
            if (!formData.nameOnCard) {
              setValidationError('Please enter the name on your card');
              return false;
            }
            if (!formData.cardValid) {
              setValidationError('Please enter valid card details');
              return false;
            }
          }
        } else {
          // Validate billing fields with Stripe Elements for non-enterprise plans
          if (!formData.nameOnCard) {
            setValidationError('Please enter the name on your card');
            return false;
          }
          if (!formData.cardValid) {
            setValidationError('Please enter valid card details');
            return false;
          }
        }
        return true;
    }

    // Default: no validation needed
    return true;
  };

  const handleNext = async () => {
    // For completion step, proceed without validation
    if (currentStep === getStepNumber('completion') && onComplete) {
      onComplete();
      return;
    }

    // Validate current step
    const isValid = validateCurrentStep();
    if (!isValid) return;

    // After Account Info step, proceed without creating the account yet.
    // Account creation (registerWithEmail) is now performed only after successful payment.
    if (currentStep === getStepNumber('account')) {
      if (existingAuthedUser) {
        setCurrentStep(getStepNumber('business'));
        return;
      }

      setCurrentStep(getStepNumber('business'));
      return;
    }

    // Handle payment processing for billing step (non-enterprise plans or enterprise with payment choice)
    if (currentStep === getStepNumber('billing') && (!isEnterprisePlan || formData.enterpriseChoice === 'payment')) {
      // Payment processing will be handled by the payment form component
      // within the Elements context
      return;
    }

    // Proceed to next step for other steps
    if (isValid && currentStep < getStepNumber('completion')) {
      // Determine the next step based on current step
      if (currentStep === getStepNumber('business')) {
        // After business, go to module levels if available, otherwise billing
        setCurrentStep(hasModulesWithLevels ? getStepNumber('moduleLevels') : getStepNumber('billing'));
      } else if (currentStep === getStepNumber('moduleLevels')) {
        // After module levels, go to billing
        setCurrentStep(getStepNumber('billing'));
      } else {
        // Default: increment by 1 for any other steps
        setCurrentStep((s) => s + 1);
      }
    }
  };
  const handlePrevious = () => {
    if (existingAuthedUser && currentStep === businessStepNumber) {
      setAccountEditDialogOpen(true);
      return;
    }

    if (currentStep > 1) {
      // Determine the previous step based on current step
      if (currentStep === getStepNumber('billing')) {
        // Before billing, go to module levels if available, otherwise business
        setCurrentStep(hasModulesWithLevels ? getStepNumber('moduleLevels') : getStepNumber('business'));
      } else if (currentStep === getStepNumber('moduleLevels')) {
        // Before module levels, go to business
        setCurrentStep(getStepNumber('business'));
      } else {
        // Default: decrement by 1 for any other steps
        setCurrentStep((s) => s - 1);
      }
    }
  };

  // Validate individual fields
  const validateEmail = (email: string): string | null => {
    if (!email?.trim()) {
      return 'Email is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validateWebsite = (website: string): string | null => {
    if (!website?.trim()) {
      return 'Website is required';
    }
    
    // Check if it's a valid URL format
    // Accept URLs with or without protocol
    const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
    
    if (!urlPattern.test(website.trim())) {
      return 'Please enter a valid website URL (e.g., https://yourcompany.com or yourcompany.com)';
    }
    
    return null;
  };

  // Handle field blur validation
  const handleEmailBlur = () => {
    const error = validateEmail(formData.email);
    setFieldErrors(prev => ({ ...prev, email: error || undefined }));
  };

  const handleWebsiteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    
    if (!value) {
      setFieldErrors(prev => ({ ...prev, website: undefined }));
      return;
    }
    
    // Only auto-format if it doesn't already have a protocol
    if (!value.match(/^https?:\/\//)) {
      const correctedValue = `https://${value}`;
      setFormData(prev => ({ ...prev, website: correctedValue }));
      value = correctedValue;
    }
    
    // Validate the final value
    const error = validateWebsite(value);
    setFieldErrors(prev => ({ ...prev, website: error || undefined }));
  };

  // Check if password meets all requirements
  const passwordMeetsRequirements = (password: string): boolean => {
    const requirements = [
      password.length >= 8,
      /\d/.test(password),
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /[!@#$%^&*(),.?":{}|<>_]/.test(password)
    ];
    return requirements.every(req => req === true);
  };

  // Handle input change for form fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Clear field-level error when user starts typing
    if (name in fieldErrors && value.trim()) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
    
    updateFormData(name, value);
  };

  // Helper function to update form data directly (for non-input fields)
  const updateFormData = (field: string, value: any) => {
    // Clear field-level error when user updates a field
    if (field in fieldErrors && value) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Memoize the card validity change handler to prevent infinite loops
  const handleCardValidityChange = useCallback((isValid: boolean) => {
    setFormData(prev => ({
      ...prev,
      cardValid: isValid
    }));
  }, []);

  // Handle password field focus
  const handlePasswordFocus = () => {
    setPasswordFocused(true);
  };

  // Handle password field blur
  const handlePasswordBlur = () => {
    // Only hide requirements if password meets all criteria
    if (passwordMeetsRequirements(formData.password)) {
      setPasswordFocused(false);
    }
  };

  const renderStepContent = () => {
    // Account Info Step
    if (currentStep === getStepNumber('account')) {
        return (
          <div className="space-y-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>Account Info</CardTitle>
              <CardDescription>
                If you need more info, please check out{' '}
                {/* DED-1412: Open help page in new tab to prevent losing form data */}
                <a href="https://help.deiterate.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Help Page
                </a>
                .
              </CardDescription>
            </CardHeader>

            {/* Subtitle for Personal Details */}
            <Label className="mb-4 block text-xs text-muted-foreground">Personal Details</Label>
            <div className="space-y-6">
              {/* Personal Details Section */}
              <div className="form-grid">
                <div>
                  <Label htmlFor="yourName" className="text-base flex items-center gap-1">
                    <span>Your Name</span>
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>
                  <Input
                    id="yourName"
                    name="yourName"
                    autoComplete="name"
                    value={formData.yourName}
                    onChange={handleInputChange}
                    className={`mt-2 w-full ${fieldErrors.yourName ? 'border-destructive focus:border-destructive' : ''}`}
                    placeholder="Enter your name"
                    aria-invalid={!!fieldErrors.yourName}
                    aria-describedby={fieldErrors.yourName ? 'yourName-error' : undefined}
                  />
                  {fieldErrors.yourName && (
                    <p id="yourName-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.yourName}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phoneNumber" className="text-base flex items-center gap-1">
                    <span>Phone Number</span>
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>
                  <PhoneInput
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(value) => updateFormData('phoneNumber', value)}
                    className={`mt-2 ${fieldErrors.phoneNumber ? '[&_input]:border-destructive [&_input]:focus:border-destructive' : ''}`}
                    placeholder="400 000 000"
                    defaultCountry="AU"
                    aria-invalid={!!fieldErrors.phoneNumber}
                    aria-describedby={fieldErrors.phoneNumber ? 'phoneNumber-error' : undefined}
                  />
                  {fieldErrors.phoneNumber && (
                    <p id="phoneNumber-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.phoneNumber}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email" className="text-base flex items-center gap-1">
                    <span>Email</span>
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    className={`mt-2 w-full ${fieldErrors.email ? 'border-destructive focus:border-destructive' : ''}`}
                    placeholder="Enter your email"
                    aria-invalid={!!fieldErrors.email}
                    aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  />
                  {fieldErrors.email && (
                    <p id="email-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password" className="text-base flex items-center gap-1">
                    <span>Password</span>
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type='password'
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                    className={`mt-2 w-full ${fieldErrors.password ? 'border-destructive focus:border-destructive' : ''}`}
                    placeholder="Enter your password"
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                  />
                  {fieldErrors.password && (
                    <p id="password-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.password}
                    </p>
                  )}
                  {/* Show password requirements while typing or if password doesn't meet requirements */}
                  {passwordFocused && (
                    <PasswordRequirements password={formData.password} />
                  )}
                </div>
              </div>

              {/* Subtitle for Business Details */}
              <Label className="mt-6 mb-4 block text-base font-medium">Business Details</Label>

              {/* Business Details Section */}
              <div className="form-grid">
                <div>
                  <Label htmlFor="companyName" className="flex items-center gap-1">
                    <span>Company Name</span>
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    placeholder="Enter your company name"
                    className={`mt-2 w-full ${fieldErrors.companyName ? 'border-destructive focus:border-destructive' : ''}`}
                    value={formData.companyName}
                    onChange={handleInputChange}
                    aria-invalid={!!fieldErrors.companyName}
                    aria-describedby={fieldErrors.companyName ? 'companyName-error' : undefined}
                  />
                  {fieldErrors.companyName && (
                    <p id="companyName-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.companyName}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="website" className="flex items-center gap-1">
                    <span>Website</span>
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    autoComplete="website"
                    placeholder="yourcompany.com or https://yourcompany.com"
                    className={`mt-2 w-full ${fieldErrors.website ? 'border-destructive focus:border-destructive' : ''}`}
                    value={formData.website}
                    onChange={(e) => {
                      // Just update the value without any auto-formatting while typing
                      handleInputChange(e);
                    }}
                    onBlur={handleWebsiteBlur}
                    aria-invalid={!!fieldErrors.website}
                    aria-describedby={fieldErrors.website ? 'website-error' : undefined}
                  />
                  {fieldErrors.website && (
                    <p id="website-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.website}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll automatically add "https://" if you don't include it
                  </p>
                </div>
              </div>

              {/* Hidden because we will use AI to fill this out */}
              {/* <div>
                <Label htmlFor="industry">Industry</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(value) => updateFormData('industry', value)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              <div className='flex flex-row gap-8'>
                <div className="w-2/5">
                  <Label className={cn("mb-4 block text-base font-medium", fieldErrors.teamSize && "text-destructive")}>
                    Specify Team Size
                    <span className="text-destructive ml-1" aria-hidden="true">*</span>
                  </Label>
                  <div className={cn("grid grid-cols-2 gap-4", fieldErrors.teamSize && "ring-2 ring-destructive rounded-lg p-1")}>
                    {['1-1', '2-10', '10-50', '50+'].map((size) => (
                      <button
                        key={size}
                        onClick={() => {
                          updateFormData('teamSize', size);
                          setFieldErrors(prev => ({ ...prev, teamSize: undefined }));
                        }}
                        className={cn(
                          'rounded-lg border-2 py-5 px-6 text-center transition-all hover:shadow-md',
                          formData.teamSize === size
                            ? 'bg-muted border-primary ring-2 ring-primary shadow-inner'
                            : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                        )}
                      >
                        <span className="text-lg">{size}</span>
                      </button>
                    ))}
                  </div>
                  {fieldErrors.teamSize && (
                    <p className="text-sm text-destructive mt-1">{fieldErrors.teamSize}</p>
                  )}
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    How many people are expected to use the platform?
                  </p>
                </div>
                <div className="w-3/5">
                  <Label className={cn("mb-4 block text-base font-medium", fieldErrors.companyPlan && "text-destructive")}>
                    Select Company Plan
                    <span className="text-destructive ml-1" aria-hidden="true">*</span>
                  </Label>
                  <RadioGroup
                    value={formData.companyPlan}
                    onValueChange={(value) => {
                      updateFormData('companyPlan', value);
                      setFieldErrors(prev => ({ ...prev, companyPlan: undefined }));
                    }}
                    className={cn("space-y-4", fieldErrors.companyPlan && "ring-2 ring-destructive rounded-lg p-1")}
                  >
                    {plans.map((plan) => {
                      // Map icon string to FontAwesome icon
                      let icon = faBuilding;
                      if (plan.icon === 'faBuildings') icon = faBuildings;
                      if (plan.icon === 'faCity') icon = faCity;

                      return (
                        <label 
                          key={plan.id}
                          htmlFor={`plan-${plan.id}`}
                          className={cn(
                            "flex items-center space-x-3 rounded-lg border p-4 transition-all hover:shadow-sm cursor-pointer",
                            formData.companyPlan === plan.id ? "border-primary bg-primary/5 shadow" : "border-gray-200 dark:border-gray-600"
                          )}
                        >
                          <FontAwesomeIcon icon={icon} className='h-6 w-6 text-primary' />
                          <div className="flex-1">
                            <div className="font-medium text-base">{plan.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {plan.description}
                            </div>
                          </div>
                          <RadioGroupItem id={`plan-${plan.id}`} value={plan.id} />
                        </label>
                      );
                    })}
                    {plans.length === 0 && (
                      <div className="p-4 text-center">
                        <Spinner size="sm" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading plans...</p>
                      </div>
                    )}
                  </RadioGroup>
                  {fieldErrors.companyPlan && (
                    <p className="text-sm text-destructive mt-1">{fieldErrors.companyPlan}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
    }

    // Business Details Step
    if (currentStep === getStepNumber('business')) {
        return (
          <div className="space-y-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>Business Details</CardTitle>
              <CardDescription>
                If you need more info, please check out{' '}
                <Link to="#" className="text-primary hover:underline">
                  Help Page
                </Link>
                .
              </CardDescription>
            </CardHeader>
            <div className="space-y-4">
              {/* Filter frameworks based on selected company plan */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {frameworks
                  .filter(framework => {
                    // Filter frameworks based on selected plan and framework's plans field
                    const frameworkPlans = (framework as any).plans || [];

                    // If framework plans includes '*' or the specific user plan, show it
                    return frameworkPlans.includes('*') || frameworkPlans.includes(formData.companyPlan);
                  })
                  .sort((a, b) => {
                    // Custom sort order for starter plans: PA, E8, SMB, DISP (PAE8SMBDISP)
                    const isStarterPlan = formData.companyPlan?.toLowerCase().includes('starter');
                    if (isStarterPlan) {
                      // Helper to normalize framework ID/name for matching
                      const normalizeFramework = (framework: typeof a): string => {
                        // Check both id and displayName for matching
                        const idLower = (framework.id || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                        const nameLower = (framework.displayName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                        
                        // Map various ID/name formats to canonical keys
                        if (idLower.includes('privacy') || nameLower.includes('privacy') || 
                            idLower === 'pa' || idLower === 'pacontrols') return 'privacyact';
                        if (idLower.includes('essential') || nameLower.includes('essential') || 
                            idLower === 'e8' || idLower === 'e8controls') return 'essential8';
                        if (idLower.includes('smb') || nameLower.includes('smb') || 
                            idLower === 'smb1001controls') return 'smb1001';
                        if (idLower.includes('disp') || nameLower.includes('disp') || 
                            idLower === 'dispcontrols') return 'disp';
                        return idLower;
                      };
                      
                      // Desired order: Privacy Act, Essential 8, SMB1001, DISP
                      const starterOrder = ['privacyact', 'essential8', 'smb1001', 'disp'];
                      const aNormalized = normalizeFramework(a);
                      const bNormalized = normalizeFramework(b);
                      const aIndex = starterOrder.indexOf(aNormalized);
                      const bIndex = starterOrder.indexOf(bNormalized);
                      
                      // If both are in the order list, sort by that order
                      if (aIndex !== -1 && bIndex !== -1) {
                        return aIndex - bIndex;
                      }
                      // If only one is in the list, prioritize it
                      if (aIndex !== -1) return -1;
                      if (bIndex !== -1) return 1;
                    }
                    // Default: alphabetical by displayName
                    return a.displayName.localeCompare(b.displayName);
                  })
                  .map((framework) => {
                    // Check if framework is selected
                    const isSelected = formData.selectedFrameworks?.includes(framework.id);

                    return (
                      <div
                        key={framework.id}
                        style={{
                          background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : '',
                          backdropFilter: 'blur(8px)'
                        }}
                        className={`relative overflow-hidden rounded-xl p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 ${isSelected
                          ? 'border-2 border-primary shadow-lg'
                          : 'bg-white/70 hover:bg-white/90 dark:bg-gray-800/70 dark:hover:bg-gray-800/90 border border-gray-100 dark:border-gray-600 shadow-sm hover:shadow-md'}`}
                        onClick={() => {
                          // Toggle framework selection
                          const currentFrameworks = formData.selectedFrameworks || [];
                          if (isSelected) {
                            updateFormData('selectedFrameworks', currentFrameworks.filter(id => id !== framework.id));
                          } else {
                            updateFormData('selectedFrameworks', [...currentFrameworks, framework.id]);
                          }
                        }}
                      >
                        {/* Light accent */}
                        <div className={`absolute inset-0 ${isSelected ? 'bg-gradient-to-tr from-primary/5 via-transparent to-primary/10' : ''}`}></div>
                        <div className="mb-4 flex justify-between items-start">
                          <div className={`p-2.5 rounded-lg ${isSelected ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                            {(() => {
                              // If there's a logo, use it
                              if (framework.logo) {
                                return <img src={framework.logo} alt={framework.displayName} className="h-5 w-5" />;
                              }

                              // For backwards compatibility - if icon object is provided directly
                              if (framework.icon) {
                                return <FontAwesomeIcon icon={framework.icon} className="h-5 w-5" />;
                              }

                              // Dynamic icon selection based on iconName
                              switch (framework.iconName) {
                                case 'building':
                                  return <FontAwesomeIcon icon={faBuilding} className="h-5 w-5" />;
                                case 'buildings':
                                  return <FontAwesomeIcon icon={faBuildings} className="h-5 w-5" />;
                                case 'city':
                                  return <FontAwesomeIcon icon={faCity} className="h-5 w-5" />;
                                case 'box-archive':
                                  return <FontAwesomeIcon icon={faBuilding} className="h-5 w-5" />;
                                default:
                                  // Default to shield if no matching icon found
                                  return <FontAwesomeIcon icon={faShield} className="h-5 w-5" />;
                              }
                            })()}
                          </div>
                        </div>
                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute bottom-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                          </div>
                        )}

                        {/* Framework title */}
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {framework.displayName}
                        </h3>

                        {/* Framework description */}
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {framework.description}
                        </p>

                        {/* Price tag for add-on modules */}
                        {(framework as any).additionalCost && (
                          <div className="absolute top-0 right-0 z-10">
                            <div className="relative group">
                              <div className="px-3 py-1 backdrop-blur-md bg-white/40 dark:bg-black/20 border border-white/50 dark:border-white/10 rounded-tr-xl rounded-bl-xl shadow-lg text-primary font-medium text-xs flex items-center gap-1.5 overflow-hidden">
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 0L9.63527 6.36473L16 8L9.63527 9.63527L8 16L6.36473 9.63527L0 8L6.36473 6.36473L8 0Z" fill="currentColor" fillOpacity="0.8" />
                                </svg>
                                <span className="text-xs font-bold">+${(framework as any).additionalCost}</span>
                              </div>
                              <div className="absolute -inset-px bg-gradient-to-r from-primary/10 via-transparent to-primary/10 rounded-tr-xl rounded-bl-xl blur-sm z-[-1]"></div>

                              {/* Tooltip */}
                              <div className="invisible opacity-0 absolute right-0 top-full z-20 mt-1 w-52 rounded-lg backdrop-blur-md bg-white/80 dark:bg-gray-800/90 border border-white/30 dark:border-white/10 p-3 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100">
                                <div className="text-xs text-gray-700 dark:text-gray-200">
                                  <p className="mb-1 font-semibold">Additional Module</p>
                                  <ul className="space-y-1.5">
                                    {formData.companyPlan === 'starter' && (
                                      <li className="flex justify-between">
                                        <span>Starter Plan:</span>
                                        <span>$99 + ${(framework as any).additionalCost}</span>
                                      </li>
                                    )}
                                    {formData.companyPlan === 'business' && (
                                      <li className="flex justify-between">
                                        <span>Business Plan:</span>
                                        <span>$1,800 + ${(framework as any).additionalCost}</span>
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Tags/Keywords */}
                        <div className="flex flex-wrap gap-2 mt-4">

                          {framework.tags && framework.tags.map(tag => (
                            <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Empty state if no frameworks are available for the selected plan */}
              {frameworks.filter(framework => {
                const frameworkPlans = (framework as any).plans || [];
                return frameworkPlans.includes('*') || frameworkPlans.includes(formData.companyPlan);
              }).length === 0 && (
                  <div className="text-center py-10">
                    <FontAwesomeIcon icon={faBuilding} className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">No frameworks available</h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No compliance frameworks are available for your selected plan.</p>
                  </div>
                )}
            </div>
          </div>
        );
    }

    // Module Levels Step (if applicable)
    if (currentStep === getStepNumber('moduleLevels') && hasModulesWithLevels) {
        const modulesWithLevels = frameworks.filter(f => 
          formData.selectedFrameworks.includes(f.id) && 
          ((f as any).levels || (f as any).levelCategories)
        );

        return (
          <div className="space-y-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>Configure Module Levels</CardTitle>
              <CardDescription>
                Set the compliance levels for your selected frameworks
              </CardDescription>
            </CardHeader>

            <div className="space-y-8">
              {modulesWithLevels.map(module => {
                const moduleData = module as any;
                
                // SMB1001 - Simple level selection
                if (module.id === 'smb1001' && moduleData.levels) {
                  return (
                    <div key={module.id} className="bg-muted/30 p-6 rounded-lg border border-border">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-500/10 p-3 rounded-full">
                          <span role="img" aria-label={module.displayName} className="text-2xl">📊</span>
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{module.displayName}</div>
                          <div className="text-xs text-muted-foreground">Select your compliance level</div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <Label className="text-base font-medium">Compliance Level</Label>
                        <Select
                          value={formData.moduleLevels[module.id] || ''}
                          onValueChange={(value) => {
                            updateFormData('moduleLevels', {
                              ...formData.moduleLevels,
                              [module.id]: value
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            {moduleData.levels.map((level: string) => (
                              <SelectItem key={level} value={level}>
                                {level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Choose the level that best matches your organization's security maturity
                        </p>
                      </div>
                    </div>
                  );
                }
                
                // DISP - Category-based levels
                if (module.id === 'disp' && moduleData.levelCategories) {
                  const dispLevels = formData.moduleLevels[module.id] || {};
                  const levelOptions = [
                    { value: 'entryLevel', label: 'Entry Level' },
                    { value: 'levelOne', label: 'Level 1' },
                    { value: 'levelTwo', label: 'Level 2' },
                    { value: 'levelThree', label: 'Level 3' }
                  ];

                  return (
                    <div key={module.id} className="bg-muted/30 p-6 rounded-lg border border-border">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-500/10 p-3 rounded-full">
                          <span role="img" aria-label={module.displayName} className="text-2xl">🛡️</span>
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{module.displayName}</div>
                          <div className="text-xs text-muted-foreground">Defence Industry Security Program</div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <Label className="text-base font-medium mb-4 block">Security Category Levels</Label>
                        {moduleData.levelCategories.map((category: string) => {
                          const categoryKey = category.replace(/\s+/g, '').replace(/\//g, '');
                          return (
                            <div key={category} className="space-y-2">
                              <Label className="text-sm font-medium">{category}</Label>
                              <Select
                                value={dispLevels[categoryKey] || ''}
                                onValueChange={(value) => {
                                  updateFormData('moduleLevels', {
                                    ...formData.moduleLevels,
                                    [module.id]: {
                                      ...dispLevels,
                                      [categoryKey]: value
                                    }
                                  });
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select level" />
                                </SelectTrigger>
                                <SelectContent>
                                  {levelOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                        <p className="text-xs text-muted-foreground mt-4">
                          Set the security level for each category based on your DISP requirements
                        </p>
                      </div>
                    </div>
                  );
                }
                
                // Essential Eight - Cascading level selection
                if (module.id === 'essential-eight' && moduleData.levels) {
                  const selectedLevels = formData.moduleLevels[module.id] || [];
                  const getHighestLevel = () => {
                    if (selectedLevels.length === 0) return 0;
                    return Math.max(...selectedLevels.map((level: string) => parseInt(level.replace('e8_level', ''))));
                  };

                  return (
                    <div key={module.id} className="bg-muted/30 p-6 rounded-lg border border-border">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500/10 p-3 rounded-full">
                          <span role="img" aria-label={module.displayName} className="text-2xl">🛡️</span>
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{module.displayName}</div>
                          <div className="text-xs text-muted-foreground">ACSC Essential Eight Maturity Model</div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <Label className="text-base font-medium">Maturity Level</Label>
                        <div className="grid grid-cols-3 gap-4">
                          {moduleData.levels.map((level: string, index: number) => {
                            const levelNum = index + 1;
                            const isSelected = getHighestLevel() >= levelNum;
                            return (
                              <button
                                key={level}
                                onClick={() => {
                                  const newLevels: string[] = [];
                                  for (let i = 0; i < levelNum; i++) {
                                    newLevels.push(moduleData.levels[i]);
                                  }
                                  updateFormData('moduleLevels', {
                                    ...formData.moduleLevels,
                                    [module.id]: newLevels
                                  });
                                }}
                                className={cn(
                                  'rounded-lg border-2 py-6 px-4 text-center transition-all hover:shadow-md',
                                  isSelected
                                    ? 'bg-muted border-primary ring-2 ring-primary shadow-inner'
                                    : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                                )}
                              >
                                <div className="text-2xl font-bold mb-2">ML{levelNum}</div>
                                <div className="text-sm">Maturity Level {levelNum}</div>
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Selecting a level automatically includes all lower levels. For example, selecting Level 3 includes Level 1 and Level 2.
                        </p>
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
            </div>
          </div>
        );
    }

    // Billing/Meeting Step
    if (currentStep === getStepNumber('billing')) {
        return (
          <div className="space-y-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>{isEnterprisePlan ? 'Schedule a Meeting' : 'Billing Details'}</CardTitle>
              <CardDescription>
                {isEnterprisePlan
                  ? 'Schedule a consultation with our enterprise team to discuss your needs.'
                  : 'If you need more info, please check out our Help Page.'}
              </CardDescription>
            </CardHeader>

            {isEnterprisePlan ? (
              // Enterprise Choice: Meeting or Immediate Payment
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <FontAwesomeIcon icon={faBuildings} className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-base">Enterprise Plan Options</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Choose how you'd like to proceed with your Enterprise plan setup.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Enterprise Choice */}
                <div className="space-y-4">
                  <Label className="text-base font-medium">Choose Your Path <span className="text-red-500">*</span></Label>

                  <div className="grid gap-4">
                    {/* Option 1: Schedule Meeting */}
                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.enterpriseChoice === 'meeting'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                        }`}
                      onClick={() => updateFormData('enterpriseChoice', 'meeting')}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="enterpriseChoice"
                          value="meeting"
                          checked={formData.enterpriseChoice === 'meeting'}
                          onChange={() => updateFormData('enterpriseChoice', 'meeting')}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium">Schedule a Consultation</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Schedule a personalised consultation with our enterprise team to discuss
                            your specific needs, custom pricing, and implementation timeline.
                          </p>
                          <div className="mt-2 text-sm font-medium text-green-600">
                            Free consultation • Custom pricing
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Option 2: Immediate Payment */}
                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.enterpriseChoice === 'payment'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                        }`}
                      onClick={() => updateFormData('enterpriseChoice', 'payment')}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="enterpriseChoice"
                          value="payment"
                          checked={formData.enterpriseChoice === 'payment'}
                          onChange={() => updateFormData('enterpriseChoice', 'payment')}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium">Get Immediate Access</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Start using the Enterprise plan immediately with full access to all features,
                            priority support, and enterprise integrations.
                          </p>
                          <div className="mt-2 text-sm font-medium text-primary">
                            $3,500/month • Immediate access • Full enterprise features
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conditional Content Based on Choice */}
                {formData.enterpriseChoice === 'meeting' && (
                  <div className="space-y-6 pt-4 border-t">
                    <div>
                      <Label htmlFor="meetingDate" className="text-base font-medium">
                        Preferred Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="meetingDate"
                        name="meetingDate"
                        type="date"
                        value={formData.meetingDate}
                        onChange={handleInputChange}
                        className="mt-2"
                        min={new Date().toISOString().split('T')[0]} // Ensure dates are today or later
                      />
                    </div>

                    <div>
                      <Label htmlFor="meetingTime" className="text-base font-medium">
                        Preferred Time <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.meetingTime || undefined}
                        onValueChange={(value) => updateFormData('meetingTime', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent>
                          {['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '13:00',
                            '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'].map(time => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="meetingTimeZone" className="text-base font-medium">
                        Time Zone
                      </Label>
                      <Select
                        value={formData.meetingTimeZone || undefined}
                        onValueChange={(value) => updateFormData('meetingTimeZone', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select your timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {['GMT-8 (Pacific)', 'GMT-7 (Mountain)', 'GMT-6 (Central)', 'GMT-5 (Eastern)',
                            'GMT-4 (Atlantic)', 'GMT+0 (UTC)', 'GMT+1 (Central European)', 'GMT+8 (China)'].map(zone => (
                              <SelectItem key={zone} value={zone}>
                                {zone}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="meetingPreferences" className="text-base font-medium">
                        Additional Notes
                      </Label>
                      <textarea
                        id="meetingPreferences"
                        name="meetingPreferences"
                        value={formData.meetingPreferences}
                        onChange={handleInputChange}
                        className="mt-2 w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Any specific topics you'd like to discuss or questions you have"
                      />
                    </div>
                  </div>
                )}

                {/* Enterprise Payment Choice */}
                {formData.enterpriseChoice === 'payment' && (
                  <div className="space-y-6 pt-4 border-t">
                    {/* Order Summary for Enterprise Payment */}
                    <Card className="border border-border">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-lg">Enterprise Plan - Immediate Access</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-medium">Enterprise Plan</h4>
                              <p className="text-sm text-muted-foreground">Full enterprise features and priority support</p>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">$3,500<span className="text-sm text-muted-foreground">/month</span></div>
                            </div>
                          </div>

                          {/* Add-ons for Enterprise */}
                          {formData.selectedFrameworks?.includes('disp') && (
                            <div className="flex justify-between items-center pt-2">
                              <div className="text-sm">
                                <div className="font-medium">DISP Module</div>
                                <div className="text-muted-foreground">Defence Industry Security Program</div>
                              </div>
                              <div className="font-medium">$199<span className="text-sm text-muted-foreground">/month</span></div>
                            </div>
                          )}

                          <div className="flex justify-between items-center pt-4 border-t font-medium">
                            <div>Total (monthly)</div>
                            <div>
                              ${3500 + (formData.selectedFrameworks?.includes('disp') ? 199 : 0)}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Payment Form for Enterprise */}
                    <Elements stripe={stripePromise}>
                      <div className="space-y-6">
                        <div>
                          <Label htmlFor="nameOnCard" className="text-base font-medium">
                            Name On Card <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="nameOnCard"
                            name="nameOnCard"
                            value={formData.nameOnCard}
                            onChange={handleInputChange}
                            className="mt-2"
                            placeholder="John Smith"
                          />
                        </div>

                        <StripePaymentForm
                          saveCard={formData.saveCard}
                          onSaveCardChange={(checked) => updateFormData('saveCard', checked)}
                          onValidityChange={handleCardValidityChange}
                        />

                        <PaymentHandler
                          formData={formData}
                          isProcessingPayment={isProcessingPayment}
                          onPaymentStart={handlePaymentStart}
                          onPaymentSuccess={handlePaymentSuccess}
                          onPaymentError={handlePaymentError}
                          isEnterprisePlan={isEnterprisePlan}
                        />
                      </div>
                    </Elements>
                  </div>
                )}
              </div>
            ) : (
              // Standard Billing UI for non-enterprise plans using Stripe Elements
              <div className="space-y-6">
                {/* Order Summary */}
                <Card className="border border-border">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Selected Plan */}
                      <div className="flex justify-between items-start border-b pb-4">
                        <div>
                          <h4 className="font-medium">
                            {plans.find(p => p.id === formData.companyPlan)?.name || 'Plan'}
                          </h4>
                          <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                            {plans.find(p => p.id === formData.companyPlan)?.features.map((feature, i) => (
                              <li key={i} className="flex items-center">
                                <Check className="h-4 w-4 text-green-500 mr-2" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formData.companyPlan === 'starter' ? '$99' :
                              formData.companyPlan === 'business' ? '$1800' :
                                formData.companyPlan === 'enterprise' ? 'Custom' : 'Free'}
                            <span className="text-sm text-muted-foreground">/month</span>
                          </div>
                        </div>
                      </div>

                      {/* Add-ons */}
                      {formData.selectedFrameworks?.includes('disp') && (
                        <div className="flex justify-between items-center pt-2">
                          <div className="text-sm">
                            <div className="font-medium">DISP Module</div>
                            <div className="text-muted-foreground">Defence Industry Security Program</div>
                          </div>
                          <div className="font-medium">$199<span className="text-sm text-muted-foreground">/month</span></div>
                        </div>
                      )}

                      {/* Total */}
                      <div className="flex justify-between items-center pt-4 border-t font-medium">
                        <div>Total (monthly)</div>
                        <div>
                          {(() => {
                            let basePrice = 0;
                            if (formData.companyPlan === 'starter') basePrice = 99;
                            else if (formData.companyPlan === 'business') basePrice = 1800;
                            else if (formData.companyPlan === 'enterprise') return 'Custom';

                            const dispAdditional = formData.selectedFrameworks?.includes('disp') ? 199 : 0;
                            const total = basePrice + dispAdditional;

                            return `$${total}`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Form */}
                <Elements stripe={stripePromise}>
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="nameOnCard" className="text-base font-medium">
                        Name On Card <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nameOnCard"
                        name="nameOnCard"
                        value={formData.nameOnCard}
                        onChange={handleInputChange}
                        className="mt-2"
                        placeholder="John Smith"
                      />
                    </div>

                    <StripePaymentForm
                      saveCard={formData.saveCard}
                      onSaveCardChange={(checked) => updateFormData('saveCard', checked)}
                      onValidityChange={handleCardValidityChange}
                    />

                    <PaymentHandler
                      formData={formData}
                      isProcessingPayment={isProcessingPayment}
                      onPaymentStart={handlePaymentStart}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                      isEnterprisePlan={isEnterprisePlan}
                    />
                  </div>
                </Elements>
              </div>
            )}
          </div>
        );
    }

    // Completion Step
    if (currentStep === getStepNumber('completion')) {
        return (
          <div className="space-y-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>You're Done!</CardTitle>
              <CardDescription>
                If you need more info, please{' '}
                <Button
                  variant="ghost"
                  className="ml-2"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                .
              </CardDescription>
            </CardHeader>

            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Your account is set up. You can now explore the platform and invite your team.
              </p>

              <Alert className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                  <strong>We need your attention!</strong>
                  <br />
                  Launch into an intuitive onboarding flow that makes configuring your de.iterate tenancy quick and easy.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        );
    }

    return null;
  };

  return (
    <div className="flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl shadow-lg bg-card">
        <CardHeader className="pb-0">
          {/* Step Indicator */}
          <div className="mb-6 flex items-center justify-between">
            {steps.map((step) => (
              <div key={step.id} className="relative flex flex-1 flex-col items-center">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-300',
                    currentStep > step.id
                      ? 'bg-primary text-white'
                      : currentStep === step.id
                        ? 'bg-primary/20 text-primary'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  )}
                >
                  {currentStep > step.id ? <Check className="h-5 w-5" /> : step.id}
                </div>
                <div
                  className={cn(
                    'mt-2 text-center text-sm font-medium',
                    currentStep >= step.id ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'
                  )}
                >
                  {step.title}
                </div>
                {step.id < steps.length && (
                  <div
                    className={cn(
                      'absolute left-[calc(50%+20px)] top-5 h-0.5 w-[calc(100%-40px)] -translate-y-1/2 bg-gray-200 dark:bg-gray-700 transition-colors duration-300',
                      currentStep > step.id && 'bg-primary/20'
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-6 md:p-8">
          {renderStepContent()}

          {/* Validation Error - Only show for non-account steps (account step uses field-level errors) */}
          {validationError && currentStep !== getStepNumber('account') && (
            <div className="mt-4 rounded-md bg-red-50 dark:bg-red-950 p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">{validationError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between border-t pt-6">
            <Button variant="outline" onClick={handlePrevious} disabled={currentStep === 1}>
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </Button>

            {currentStep < getStepNumber('completion') ? (
              // Hide Next button ONLY on billing step for non-enterprise (payment form has its own button)
              (currentStep === getStepNumber('billing') && !isEnterprisePlan) ? null : (
                <Button onClick={handleNext} disabled={isRegistering || registrationInProgress}>
                  {isRegistering || registrationInProgress ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>
                        {currentStep === getStepNumber('account') ? 'Creating Account...' :
                          currentStep === getStepNumber('billing') ? 'Processing Payment...' : 'Processing...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>
                        {currentStep === getStepNumber('account') ? 'Continue' :
                          currentStep === getStepNumber('business') ? 'Continue' :
                          currentStep === getStepNumber('moduleLevels') ? 'Continue' :
                          currentStep === getStepNumber('billing') && !isEnterprisePlan ? 'Process Payment & Continue' :
                          currentStep === getStepNumber('billing') ? 'Submit' : 'Continue'}
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )
            ) : (
              <Button onClick={() => {
                (onComplete ? onComplete() : null)
                navigate('/login')
              }}>
                <span>Go to Login</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <AuthProvider>
        {/* Tenancy Creation Dialog */}
        <TenancyCreationDialog
          isOpen={showTenancyDialog}
          onClose={() => {
            setShowTenancyDialog(false);
            // Reset phase state when dialog closes
            setSetupPhase('account');
            setAccountProgress(0);
            setAccountError(null);
          }}
          formData={formData}
          onComplete={async (customerID) => {
            setShowTenancyDialog(false);
            setCurrentStep(4); // Go to completion step
            console.log('Tenancy created successfully:', customerID);
            await triggerDashboardRefresh();
            if (onComplete) {
              onComplete();
            }
          }}
          onFixRequested={handleFixRequested}
          phase={setupPhase}
          accountProgress={accountProgress}
          accountError={accountError}
        />
      </AuthProvider>

      <Dialog open={fixWebsiteDialogOpen} onOpenChange={setFixWebsiteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Fix Website URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {websiteFixMessage && (
              <Alert variant="destructive">
                <AlertDescription>{websiteFixMessage}</AlertDescription>
              </Alert>
            )}
            <Label htmlFor="website-fix">Website</Label>
            <Input
              id="website-fix"
              value={websiteFixValue}
              onChange={(e) => setWebsiteFixValue(e.target.value)}
              placeholder="https://yourcompany.com"
            />
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={handleCancelWebsiteFix}>
              Cancel
            </Button>
            <Button onClick={handleApplyWebsiteFix} disabled={!websiteFixValue.trim()}>
              Apply Fix & Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={accountEditDialogOpen} onOpenChange={setAccountEditDialogOpen}>
        <DialogContent className="max-w-6xl! bg-card">
          <DialogHeader>
            <DialogTitle>Update Your Account Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              You are signed in as <strong>{formData.email || 'your registered email'}</strong>. Update your business details below. Email and password changes require restarting onboarding.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="account-edit-company-name">Company Name</Label>
                <Input
                  id="account-edit-company-name"
                  value={accountEditDraft.companyName}
                  onChange={(e) => handleAccountEditDraftChange('companyName', e.target.value)}
                  placeholder="Enter your company name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-edit-website">Company Website / Domain</Label>
                <Input
                  id="account-edit-website"
                  value={accountEditDraft.website}
                  onChange={(e) => handleAccountEditDraftChange('website', e.target.value)}
                  onBlur={(e) => handleAccountEditDraftChange('website', normalizeWebsite(e.target.value))}
                  placeholder="https://yourcompany.com"
                />
                <p className="text-xs text-muted-foreground">We'll automatically add "https://" if you don't include it.</p>
              </div>

              <div className="space-y-2">
                <Label>Team Size</Label>
                <div className="grid grid-cols-2 gap-4">
                  {['1-1', '2-10', '10-50', '50+'].map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => handleAccountEditDraftChange('teamSize', size)}
                      className={cn(
                        'rounded-lg border-2 py-5 px-6 text-center transition-all hover:shadow-md',
                        accountEditDraft.teamSize === size
                          ? 'bg-muted border-primary ring-2 ring-primary shadow-inner'
                          : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                      )}
                    >
                      <span className="text-lg">{size}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">How many people are expected to use the platform?</p>
              </div>

              <div className="space-y-2">
                <Label>Company Plan</Label>
                <div className="space-y-3">
                  {plans.map((plan) => {
                    let icon = faBuilding;
                    if (plan.icon === 'faBuildings') icon = faBuildings;
                    if (plan.icon === 'faCity') icon = faCity;

                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handleAccountEditDraftChange('companyPlan', plan.id)}
                        className={cn(
                          'w-full flex items-start space-x-3 rounded-lg border p-4 transition hover:shadow-sm text-left',
                          accountEditDraft.companyPlan === plan.id ? 'border-primary bg-primary/5 shadow' : 'border-border'
                        )}
                      >
                        <FontAwesomeIcon icon={icon} className='h-6 w-6 text-primary mt-1' />
                        <div className="flex-1">
                          <div className="font-medium">{plan.name}</div>
                          <div className="text-sm text-muted-foreground">{plan.description}</div>
                        </div>
                        <div className="mt-1">
                          <div className={cn('h-4 w-4 rounded-full border', accountEditDraft.companyPlan === plan.id ? 'bg-primary border-primary' : 'border-muted')}></div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-6">
            <Button variant="outline" onClick={() => setAccountEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRestartOnboarding}>
              Restart Onboarding
            </Button>
            <Button onClick={handleApplyAccountEdits}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
